






































































































































































































































































































































































import Vue from "vue";
import dayjs from "dayjs";
import eventBus from "@/services/eventBus";
import base64toUInt8Array, { openPdfInNewTab } from "@/utils/fileConvert";
import DateInput from "@/components/DateInput.vue";
import CheckBox from "@/components/CheckBox.vue";
import api from "../services/api";
import {
  ReportHeadOption,
  ReportTreeNode,
  RunReportParams,
  DefaultRunReportParams,
  ReportResultRow,
  ReportAccountRow,
  ReportRunRowUpdate,
  ReportAccountCheckRow,
  ReportLangOptions,
} from "../models/ReportRun";

interface TreeItem {
  key: string;
  label: string;
  children?: TreeItem[];
}

interface Data {
  params: RunReportParams;
  issueDate: string;
  lang: number;
  langOptions: { id: number; name: string }[];
  accountCheck: boolean;
  accountCheckRows: ReportAccountCheckRow[];
  reportHeadOptions: ReportHeadOption[];
  tree: ReportTreeNode[];
  selectedTreeKeys: string[];
  openNodes: string[];
  rows: ReportResultRow[];
  accountRows: ReportAccountRow[];
  selectedRow: ReportResultRow | null;
  runId: number;
  accnoEnabled: boolean;
  showAccountDetail: boolean;
  zeroItemVisible: boolean;
  editMode: boolean;
  running: boolean;
  exporting: boolean;
  columns: any[];
  accountColumns: any[];
}
interface Methods {
  loadReportHeads(): Promise<void>;
  loadTree(): Promise<void>;
  expandAll(): void;
  collapseAll(): void;
  collectKeys(items: TreeItem[], acc: string[]): void;
  run(): Promise<void>;
  doRun(): Promise<void>;
  loadRows(): Promise<void>;
  onRowClick(e: any): void;
  loadAccountDetail(): Promise<void>;
  markModified(row: ReportResultRow): void;
  normalizeValue(row: ReportResultRow, field: "previousYear" | "previousYearMod" | "currentYear"): void;
  toggleEject(row: ReportResultRow): void;
  saveModifiedRows(): Promise<boolean>;
  printPdf(): Promise<void>;
  exportExcel(): Promise<void>;
  reset(): void;
  formatValue(v: number | null): string;
  rowStyle(row: ReportResultRow): object;
}
interface Computed {
  title: string;
  reportType: number;
  treeItems: TreeItem[];
}
interface Props {
  mode: string;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { DateInput, CheckBox },

  // 'M' = Mérleg, 'E' = Eredménykimutatás (K2D w_popup_report_bokrep is_mit)
  props: {
    mode: { type: String, default: "M" },
  },

  data(): Data {
    return {
      params: { ...DefaultRunReportParams },
      issueDate: dayjs().format("YYYY-MM-DD"),
      lang: 1,
      langOptions: ReportLangOptions,
      accountCheck: false,
      accountCheckRows: [],
      reportHeadOptions: [],
      tree: [],
      selectedTreeKeys: [],
      openNodes: [],
      rows: [],
      accountRows: [],
      selectedRow: null,
      runId: 0,
      accnoEnabled: false,
      showAccountDetail: false,
      zeroItemVisible: false,
      editMode: true,
      running: false,
      exporting: false,
      // K2D d_g_imboktemprep oszlopsorrend: order_no (Sorszám) → eject_after (Lapdobás) → code (Kód)
      columns: [
        { field: "orderNo", title: "Sorsz.", width: "60px", cell: "orderNoTemplate" },
        { field: "ejectAfter", title: "Lapdobás", width: "85px", cell: "ejectTemplate", headerClassName: "center_text" },
        { field: "code", title: "Kód", width: "140px", cell: "codeTemplate" },
        { field: "name", title: "Megnevezés", cell: "nameTemplate" },
        { field: "previousYear", title: "Előző év", width: "150px", cell: "previousYearTemplate" },
        { field: "previousYearMod", title: "Előző év módosításai", width: "160px", cell: "previousYearModTemplate" },
        { field: "currentYear", title: "Tárgyév", width: "150px", cell: "currentYearTemplate" },
      ],
      accountColumns: [
        { field: "accno", title: "Főkönyvi szám", width: "140px" },
        { field: "name", title: "Megnevezés" },
        { field: "previousYear", title: "Előző év", width: "150px", cell: "accPreviousYearTemplate" },
        { field: "previousYearMod", title: "Előző év módosításai", width: "160px", cell: "accPreviousYearModTemplate" },
        { field: "currentYear", title: "Tárgyév", width: "150px", cell: "accCurrentYearTemplate" },
      ],
    };
  },

  async beforeMount() {
    this.params.mode = this.mode;
    await this.loadReportHeads();
  },

  computed: {
    title(): string {
      return this.mode === "E" ? "Eredménykimutatás" : "Mérleg";
    },
    // K2D: hmbokrephead.reptype — M→2, E→3
    reportType(): number {
      return this.mode === "E" ? 3 : 2;
    },
    treeItems(): TreeItem[] {
      const map = (nodes: ReportTreeNode[]): TreeItem[] =>
        nodes.map(n => ({
          key: `${n.level}-${n.id}`,
          label: `${n.code ? n.code + " " : ""}${n.name || ""}`,
          children: n.children && n.children.length > 0 ? map(n.children) : undefined,
        }));
      const items = map(this.tree);
      if (items.length === 0) return [];

      // K2D ue_tree: a fa gyökere maga a riport (a hmbokrephead neve), és az ÖSSZES főcsoport
      // az alatt van — a gyerek nélküli "...összesen" főcsoportok is, levélként. A gyökér
      // checkboxa a natív treeview-kaszkáddal az összes sort kijelöli (K2D ue_setchildstate).
      const head = this.reportHeadOptions.find(h => h.id === this.params.reportHeadId);
      return [
        {
          key: `head-${this.params.reportHeadId}`,
          label: head ? head.name : this.title,
          children: items,
        },
      ];
    },
  },

  methods: {
    async loadReportHeads() {
      this.reportHeadOptions = await api.GetReportHeads(this.reportType);
      if (this.reportHeadOptions.length === 0) {
        // K2D: MSG_MERLEG / MSG_EREDM — nincs a típushoz riport-definíció
        eventBus.$emit("error", `Nincs ${this.title.toLowerCase()} típusú riport-definíció!`);
        return;
      }
      this.params.reportHeadId = this.reportHeadOptions[0].id;
    },

    async loadTree() {
      if (!this.params.reportHeadId) {
        this.tree = [];
        return;
      }
      this.tree = await api.GetReportTree(this.params.reportHeadId);
      this.selectedTreeKeys = [];
      this.expandAll();
    },

    collectKeys(items: TreeItem[], acc: string[]) {
      for (const item of items) {
        if (item.children && item.children.length > 0) {
          acc.push(item.key);
          this.collectKeys(item.children, acc);
        }
      }
    },

    expandAll() {
      const keys: string[] = [];
      this.collectKeys(this.treeItems, keys);
      this.openNodes = keys;
    },

    // A fő szülő (gyökér) nyitva marad, hogy a közvetlen gyerekei (a valódi főcsoportok) látszódjanak.
    collapseAll() {
      this.openNodes = this.treeItems.filter(i => i.children && i.children.length > 0).map(i => i.key);
    },

    // K2D pb_ok: fksz_ell='+' esetén futtatás előtt ellenőrző lista (w_popup_koltsegmatrix_ell)
    async run() {
      if (!this.params.reportHeadId || this.params.reportHeadId <= 1) {
        eventBus.$emit("error", "Válasszon riportot!");
        return;
      }
      if (!this.params.fromDate || !this.params.toDate) {
        eventBus.$emit("error", "Adja meg az intervallumot!");
        return;
      }

      // Fksz. ellenőrzés: nem szakítja meg a futtatást (nincs modal) — a háttérben fut le,
      // és csak jelez, ha van a riport fksz-maszkjaiba nem eső könyvelt tétel.
      if (this.accountCheck) {
        try {
          this.accountCheckRows = await api.GetReportAccountCheck({
            reportHeadId: this.params.reportHeadId,
            mode: this.mode,
            fromDate: this.params.fromDate,
            toDate: this.params.toDate,
          });
          if (this.accountCheckRows.length > 0) {
            eventBus.$emit(
              "error",
              `Fksz. ellenőrzés: ${this.accountCheckRows.length} könyvelt tétel nem esik bele a riport fksz-maszkjaiba!`
            );
          }
        } catch (error) {
          console.error(error);
          eventBus.$emit("error", "Az fksz. ellenőrzés nem sikerült!");
          return;
        }
      }

      await this.doRun();
    },

    async doRun() {
      this.params.mode = this.mode;
      this.params.selectedRowIds = this.selectedTreeKeys
        .filter(k => k.startsWith("row-"))
        .map(k => Number(k.substring(4)));

      this.running = true;
      try {
        const result = await api.RunReport(this.params);
        if (!result.success) {
          eventBus.$emit("error", result.message || "A riport futtatása nem sikerült!");
          return;
        }
        this.runId = result.runId;
        this.accnoEnabled = result.accnoEnabled;
        // K2D cbx_accno.checked=true: ha volt bejelölt sor, a bontó tábla azonnal látszik
        this.showAccountDetail = result.accnoEnabled;
        this.zeroItemVisible = false;
        this.accountRows = [];
        this.selectedRow = null;
        await this.loadRows();
      } catch (error) {
        console.error(error);
        eventBus.$emit("error", "A riport futtatása nem sikerült!");
      } finally {
        this.running = false;
      }
    },

    async loadRows() {
      // A létező (szerkeszthető cellához tartozó, id>0) üres értékek 0-ként jelenjenek meg;
      // a cella nélküli (cím/elválasztó) sorok maradnak üresen.
      const selectedId = this.selectedRow ? this.selectedRow.row : null;
      this.rows = (await api.GetReportRows(this.runId, this.mode, this.lang)).map(r => ({
        ...r,
        previousYear: r.previousYearId != null && r.previousYearId > 0 && r.previousYear == null ? 0 : r.previousYear,
        previousYearMod: r.previousYearModId != null && r.previousYearModId > 0 && r.previousYearMod == null ? 0 : r.previousYearMod,
        currentYear: r.currentYearId > 0 && r.currentYear == null ? 0 : r.currentYear,
        modified: false,
        // Vue 2: a kiemelés-flaget itt kell felvenni, utólagos hozzáadása nem lenne reaktív
        selected: false,
      }));
      // Nyelvváltásnál a sorok újratöltődnek – a kijelölés (és vele a bontó tábla) maradjon a helyén
      this.selectedRow = selectedId != null ? this.rows.find(r => r.row === selectedId) ?? null : null;
      if (this.selectedRow) this.selectedRow.selected = true;
    },

    // K2D dw_szerkeszt::rowfocuschanged — a kiválasztott sor fksz-albontása
    onRowClick(e: any) {
      // K2D inv_rowselect single: a fókuszált sor kiemelve marad, így látszik,
      // melyik sorhoz tartozik az alatta lévő fksz-bontás
      this.rows.forEach(r => (r.selected = r === e.dataItem));
      this.selectedRow = e.dataItem;
      if (this.showAccountDetail) {
        this.loadAccountDetail();
      }
    },

    async loadAccountDetail() {
      if (!this.selectedRow || this.runId === 0) {
        this.accountRows = [];
        return;
      }
      // K2D: a 0 értékű sor albontását a "0-ás tételek is" pipa 1-re cserélt értékkel kéri
      let value1 = this.selectedRow.previousYear ?? 0;
      let value3 = this.selectedRow.currentYear ?? 0;
      if (this.zeroItemVisible) {
        if (value1 === 0) value1 = 1;
        if (value3 === 0) value3 = 1;
      }
      this.accountRows = await api.GetReportAccountDetail({
        runId: this.runId,
        row: this.selectedRow.row,
        mode: this.mode,
        value1,
        value3,
        lang: this.lang,
      });
    },

    markModified(row: ReportResultRow) {
      row.modified = true;
    },

    // Kiürített érték-mezőbe a mezőből kilépve mindig 0 kerül — üres érték nem maradhat.
    normalizeValue(row: ReportResultRow, field: "previousYear" | "previousYearMod" | "currentYear") {
      const v = row[field] as any;
      if (v === "" || v === null || v === undefined || (typeof v === "number" && isNaN(v))) {
        row[field] = 0;
        this.markModified(row);
      }
    },

    toggleEject(row: ReportResultRow) {
      row.ejectAfter = !row.ejectAfter;
      this.markModified(row);
    },

    // K2D pb_print eleje: a módosított sorok visszaírása az imboktemprep-be
    async saveModifiedRows(): Promise<boolean> {
      const modified = this.rows.filter(r => r.modified);
      if (modified.length === 0) return true;
      const updates: ReportRunRowUpdate[] = modified.map(r => ({
        previousYearId: r.previousYearId,
        previousYearModId: r.previousYearModId,
        currentYearId: r.currentYearId,
        previousYear: r.previousYear ?? 0,
        previousYearMod: r.previousYearMod ?? 0,
        currentYear: r.currentYear ?? 0,
        orderNo: r.orderNo,
        ejectAfter: r.ejectAfter,
      }));
      const result = await api.UpdateReportRows(updates);
      if (result !== "Success") {
        eventBus.$emit("error", "A módosított sorok mentése nem sikerült: " + result);
        return false;
      }
      this.rows.forEach(r => (r.modified = false));
      return true;
    },

    async printPdf() {
      this.exporting = true;
      // Egész oldalas töltésjelző a riport-PDF elkészültéig (BusyIndicator overlay)
      eventBus.$emit("busy-message", true);
      try {
        if (!(await this.saveModifiedRows())) return;
        const base64 = await api.GetReportPdf({
          runId: this.runId,
          mode: this.mode,
          inThousands: this.params.inThousands,
          filler: this.params.filler,
          issueDate: this.issueDate,
          zeroItemVisible: this.zeroItemVisible,
          lang: this.lang,
        });
        if (!base64) {
          eventBus.$emit("error", "A riport nyomtatása nem sikerült!");
          return;
        }
        openPdfInNewTab(base64toUInt8Array(base64));
      } catch (error) {
        console.error(error);
        eventBus.$emit("error", "A riport nyomtatása nem sikerült!");
      } finally {
        eventBus.$emit("busy-message", false);
        this.exporting = false;
      }
    },

    async exportExcel() {
      this.exporting = true;
      try {
        if (!(await this.saveModifiedRows())) return;
        const file = await api.GetReportExcel(this.runId, this.mode, this.lang);
        if (!file) {
          eventBus.$emit("error", "Az Excel export nem sikerült!");
          return;
        }
        const downloadLink = document.createElement("a");
        downloadLink.href = `data:application/vnd.openxmlformats;base64,${file}`;
        downloadLink.download = `${this.title}_${this.runId}.xlsx`;
        downloadLink.click();
      } catch (error) {
        console.error(error);
        eventBus.$emit("error", "Az Excel export nem sikerült!");
      } finally {
        this.exporting = false;
      }
    },

    // K2D pb_urit: paraméterek alaphelyzetbe (az eredményt nem törli)
    reset() {
      const reportHeadId = this.reportHeadOptions.length > 0 ? this.reportHeadOptions[0].id : 0;
      this.params = { ...DefaultRunReportParams, mode: this.mode, reportHeadId };
      this.issueDate = dayjs().format("YYYY-MM-DD");
      this.lang = 1;
      this.accountCheck = false;
      this.selectedTreeKeys = [];
    },

    formatValue(v: number | null): string {
      if (v === null || v === undefined) return "";
      return v.toLocaleString("hu-HU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    // K2D d_g_imboktemprep: is_bold / is_italic a definíciós szintből
    rowStyle(row: ReportResultRow): object {
      return {
        fontWeight: row.isBold ? 700 : 400,
        fontStyle: row.isItalic ? "italic" : "normal",
      };
    },
  },

  watch: {
    // ugyanaz a komponens szolgálja ki a /balance-report és /income-statement-report route-ot
    mode() {
      this.rows = [];
      this.accountRows = [];
      this.runId = 0;
      this.accnoEnabled = false;
      this.showAccountDetail = false;
      this.zeroItemVisible = false;
      this.selectedRow = null;
      this.tree = [];
      this.selectedTreeKeys = [];
      this.accountCheck = false;
      this.params = { ...DefaultRunReportParams, mode: this.mode };
      this.loadReportHeads();
    },
    // K2D dw_1::itemchanged 'rephead' ága: riportfej-váltásra újraépül a fa
    "params.reportHeadId"() {
      this.loadTree();
    },
    // nyelv-váltás: a megjelenített sorok újratöltése az új nyelven
    async lang() {
      if (this.runId === 0) return;
      await this.loadRows();
      // a fksz-bontás megnevezései is nyelvfüggők (decode(langId, national, english, foreign1))
      if (this.showAccountDetail) this.loadAccountDetail();
    },
    // K2D cbx_accno / cbx_zero_item_visible: azonnali újratöltés
    showAccountDetail() {
      if (this.showAccountDetail) this.loadAccountDetail();
    },
    zeroItemVisible() {
      if (this.showAccountDetail) this.loadAccountDetail();
    },
  },
});
